import { Menu, MenuButton, MenuItem, MenuItems } from "@headlessui/react";
import { FormattedMessage as FM } from "react-intl";
import { addMonths } from "date-fns";
import { signal } from "@preact/signals-react";

import useLangStore from "store/langStore";
import dates from "componentsv2/tools/dates";
import { classNames } from "tools/ui";
import { Spanish } from "flatpickr/dist/l10n/es";

import {
  ChevronDownIcon,
  EllipsisHorizontalIcon,
  CalendarIcon,
  UserIcon,
  XMarkIcon,
} from "components/Icons";
import { useContext, setTimeData } from "../store/useContext";

const events_day = signal([]);

export default function DetailView({
  newDays,
  handleEvent,
  handleDay,
  onClose,
}) {
  const { locale } = useLangStore();
  const { context, updateContext } = useContext();
  const { currentDate } = context;
  const fmCalendar = Spanish;
  const cdate = dates.fmt(currentDate, "yyyy-MM-dd");

  events_day.value = newDays.flatMap((day) =>
    day.dayString === cdate ? day.events : [],
  );
  const today = new Date();
  const normalize = (date) =>
    new Date(date.getFullYear(), date.getMonth(), date.getDate());

  const button_disabled = normalize(currentDate) < normalize(today);

  async function onChangeDate(direction) {
    const diff = direction === "previous" ? -1 : 1;
    updateContext({ currentDate: addMonths(currentDate, diff) });
    await setTimeData();
  }

  function getEvents(e, day) {
    e.stopPropagation();
    e.preventDefault();
    updateContext({ currentDate: day.day });
    events_day.value = day.events;
  }

  function getDateTimeFormat(date) {
    const date_ = new Date(date);
    const formattedDate = date_.toLocaleDateString("es-ES", {
      weekday: "long",
      year: "numeric",
      month: "long",
      day: "numeric",
    });

    const formattedTime = date_.toLocaleTimeString("es-ES", {
      hour: "2-digit",
      minute: "2-digit",
      hour12: true,
    });
    return `${formattedDate} a las ${formattedTime}`;
  }

  return (
    <div>
      <div className="flex mb-4 justify-between sticky top-0">
        <h1 className="ml-3 text-neutral-500 text-3xl my-auto">
          <FM id={"upcoming_meetings"} />
        </h1>
        <div
          className="ml-auto cursor-pointer active:scale-95"
          onClick={onClose}
        >
          <XMarkIcon
            width="32"
            className="fill-current text-neutral-500 hover:text-red-600 active:text-blue-300"
          />
        </div>
      </div>
      <div className="lg:grid lg:grid-cols-12 lg:gap-x-16 md:mx-4">
        <div className="text-center lg:col-start-8 lg:col-end-13 lg:row-start-1 xl:col-start-9">
          <div className="flex items-center text-gray-900">
            <button
              type="button"
              onClick={() => onChangeDate("previous")}
              className="-m-1.5 flex flex-none items-center justify-center p-1.5 text-gray-400 hover:text-gray-500"
            >
              <span className="sr-only">
                <FM id={"previous_month"} />
              </span>
              <ChevronDownIcon
                className="size-5 rotate-90"
                aria-hidden="true"
              />
            </button>
            <div className="flex-auto text-sm font-semibold">
              {currentDate.toLocaleDateString(locale, { month: "long" })}
            </div>
            <button
              type="button"
              onClick={() => onChangeDate("next")}
              className="-m-1.5 flex flex-none items-center justify-center p-1.5 text-gray-400 hover:text-gray-500"
            >
              <span className="sr-only">
                <FM id={"next_month"} />
              </span>
              <ChevronDownIcon
                className="size-5 -rotate-90"
                aria-hidden="true"
              />
            </button>
          </div>
          <div className="mt-6 grid grid-cols-7 text-xs/6 text-gray-500">
            {fmCalendar.weekdays.shorthand.map((day) => (
              <div key={day} className="bg-white py-2">
                <span className="sr-only">{day}</span>
                {day[0]}
              </div>
            ))}
          </div>
          <div className="isolate mt-2 grid grid-cols-7 gap-px rounded-lg bg-gray-200 text-sm ring-1 shadow-sm ring-gray-200">
            {newDays.map((day, dayIdx) => (
              <button
                key={day.dayString}
                type="button"
                onClick={(e) => getEvents(e, day)}
                className={classNames(
                  "py-1.5 hover:bg-gray-100 focus:z-10",
                  day.dayString === cdate
                    ? "bg-gray-100"
                    : day.isCurrentMonth
                      ? "bg-white"
                      : "bg-gray-50",
                  (day.dayString === cdate || day.isToday) && "font-semibold",
                  !(day.dayString === cdate) &&
                    day.isCurrentMonth &&
                    !day.isToday &&
                    "text-gray-900",
                  !(day.dayString === cdate) &&
                    !day.isCurrentMonth &&
                    !day.isToday &&
                    "text-gray-400",
                  day.isToday && "text-indigo-600",
                  dayIdx === 0 && "rounded-tl-lg",
                  dayIdx === 6 && "rounded-tr-lg",
                  dayIdx === newDays.length - 7 && "rounded-bl-lg",
                  dayIdx === newDays.length - 1 && "rounded-br-lg",
                )}
              >
                <time
                  dateTime={day.date}
                  className={classNames(
                    "mx-auto flex size-7 items-center justify-center rounded-full",
                    day.isSelected && day.isToday && "bg-indigo-600",
                    day.isSelected && !day.isToday && "bg-gray-900",
                  )}
                >
                  {dates.fmt(day.day, "d")}
                </time>
              </button>
            ))}
          </div>
          <button
            type="button"
            onClick={(e) => handleDay(e)}
            className="my-8 w-full rounded-md bg-sky-950 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-indigo-500 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600 disabled:opacity-80"
          >
            <FM id={"schedule_appointment"} />
          </button>
        </div>
        <ol className="text-sm/6 lg:col-span-7 xl:col-span-8">
          {events_day.value.map((event) => (
            <li
              key={event.id}
              className="relative flex gap-6 not-first:py-6 first:pb-6 xl:static"
            >
              <div className="flex-auto">
                <h3 className="pr-10 font-semibold text-gray-900 xl:pr-0">
                  {event.name}
                </h3>
                <dl className="mt-2 flex flex-col text-gray-500 xl:flex-row">
                  <div className="flex items-start gap-x-3">
                    <dt className="mt-0.5">
                      <span className="sr-only">Date</span>
                      <CalendarIcon
                        className="size-5 text-gray-400"
                        aria-hidden="true"
                      />
                    </dt>
                    <dd>
                      <time dateTime={event.start_date}>
                        {getDateTimeFormat(event.start_date)}
                      </time>
                    </dd>
                  </div>
                  <div className="mt-2 flex items-start gap-x-3 xl:mt-0 xl:ml-3.5 xl:border-l xl:border-gray-400/50 xl:pl-3.5">
                    <dt className="mt-0.5">
                      <span className="sr-only">User</span>
                      <UserIcon
                        className="size-5 text-gray-400"
                        aria-hidden="true"
                      />
                    </dt>
                    <dd>{event.patient}</dd>
                  </div>
                </dl>
              </div>
              <Menu
                as="div"
                className="absolute right-0 xl:relative xl:top-auto xl:right-auto xl:self-center"
              >
                <div>
                  <MenuButton className="-m-2 flex items-center rounded-full p-2 text-gray-500 hover:text-gray-600">
                    <span className="sr-only">Open options</span>
                    <EllipsisHorizontalIcon
                      className="size-5"
                      aria-hidden="true"
                    />
                  </MenuButton>
                </div>

                <MenuItems
                  transition
                  className="absolute right-0 z-10 mt-2 w-36 origin-top-right rounded-md bg-white ring-1 shadow-lg ring-black/5 transition focus:outline-hidden data-closed:scale-95 data-closed:opacity-0 data-enter:duration-100 data-enter:ease-out data-leave:duration-75 data-leave:ease-in"
                >
                  <div className="py-1">
                    <MenuItem>
                      <a
                        onClick={(e) => handleEvent(e, event)}
                        className="block px-4 py-2 text-sm text-gray-700 data-focus:bg-gray-100 data-focus:text-gray-900 data-focus:outline-hidden"
                      >
                        <FM id="appointment_scheduling.edit" />
                      </a>
                    </MenuItem>
                  </div>
                </MenuItems>
              </Menu>
            </li>
          ))}
        </ol>
      </div>
    </div>
  );
}
